import React, { useState } from "react";
import {
  Container,
  Typography,
  TextField,
  Button,
  Grid,
  MenuItem,
  CircularProgress,
} from "@mui/material";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import * as Yup from "yup";
import { useFormik } from "formik";
import { Asterisk } from "../components/Common/ui/Asterisk";

// Theme setup
const staticPageTheme = createTheme({
  palette: {
    primary: { main: "#4540DB" },
    secondary: { main: "#0044ff" },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: { textTransform: "none" },
      },
    },
  },
});

// Static data
const roles = [
  { value: "admin", label: "Admin" },
  { value: "user", label: "User" },
  { value: "guest", label: "Guest" },
];
const companySizes = [
  { value: "small", label: "Small" },
  { value: "medium", label: "Medium" },
  { value: "large", label: "Large" },
];
const industries = [
  { value: "tech", label: "Technology" },
  { value: "finance", label: "Finance" },
  { value: "healthcare", label: "Healthcare" },
];

// Form validation schema
const ContactUsSchema = Yup.object({
  firstName: Yup.string().required("Please enter your first name"),
  email: Yup.string()
    .email("Please enter a valid email address")
    .required("Email is required"),
  phoneNumber: Yup.string().required("Phone number is required"),
  extention: Yup.string()
    .matches(/^\d+$/, "Extension must be a number")
    .max(5, "Extension can't exceed 5 digits"), // Optional validation
  message: Yup.string().required("Please enter a message"),
});

const ContactPage = () => {
  const [isSubmitting, setSubmitting] = useState(false);

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      role: "",
      phoneNumber: "",
      extention: "",
      email: "",
      companyName: "",
      companySize: "",
      industry: "",
      message: "",
    },
    validationSchema: ContactUsSchema,
    onSubmit: (values) => {
      setSubmitting(true);
      setTimeout(() => {
        setSubmitting(false);
      }, 2000); // Simulate an API call
    },
  });

  return (
    <ThemeProvider theme={staticPageTheme}>
      <Container maxWidth="xl" sx={{ marginTop: "20px" }}>
        <Grid item md={12} className={"contactus-grid"}>
          <Typography
            textAlign={"center"}
            sx={{
              fontSize: "40px",
              fontWeight: "600",
            }}
          >
            Experience a live Eximius AI
          </Typography>
          <Typography
            textAlign={"center"}
            sx={{
              fontSize: "40px",
              fontWeight: "600",
              marginTop: "-18px",
            }}
          >
            demo and get answers
          </Typography>
        </Grid>
        <Container>
          <Grid container alignItems="center" justifyContent="center">
            <Grid item md={6}>
              <form onSubmit={formik.handleSubmit}>
                {/* First Name */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  First Name <Asterisk />
                </Typography>
                <TextField
                  name="firstName"
                  placeholder="Your First Name"
                  fullWidth
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.firstName && Boolean(formik.errors.firstName)
                  }
                  helperText={
                    formik.touched.firstName && formik.errors.firstName
                  }
                  sx={{ marginTop: "5px" }}
                />

                {/* Last Name */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  Last Name
                </Typography>
                <TextField
                  name="lastName"
                  placeholder="Your Last Name"
                  fullWidth
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ marginTop: "5px" }}
                />

                {/* Role */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  Your Role
                </Typography>
                <TextField
                  name="role"
                  select
                  fullWidth
                  value={formik.values.role}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ marginTop: "5px" }}
                >
                  {roles.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Phone Number and Extension */}
                <Grid container spacing={2} >
                  {/* Phone Number Field */}
                  <Grid item xs={12} sm={8} marginTop="20px">
                    <Typography
                      color="inherit"
                      textAlign="left"
                      marginBottom="5px"
                    >
                      Phone number <Asterisk />
                    </Typography>
                    <PhoneInput
                      country={"us"}
                      value={formik.values.phoneNumber}
                      onChange={(value) =>
                        formik.setFieldValue("phoneNumber", value)
                      }
                      inputStyle={{
                        borderColor: "",
                        width: "100%",
                        height: "55px",
                      }}
                      preserveOrder={["preferredCountries"]}
                    />
                    {formik.touched.phoneNumber &&
                      formik.errors.phoneNumber && (
                        <Typography color="error">
                          {formik.errors.phoneNumber}
                        </Typography>
                      )}
                  </Grid>

                  {/* Extension Field */}
                  <Grid item xs={12} sm={4}>
                    <Typography
                      color="inherit"
                      textAlign="left"
                      marginBottom="5px"
                      marginTop="20px"
                    >
                      Extention
                    </Typography>
                    <TextField
                      name="extention"
                      placeholder="Ext."
                      fullWidth
                      value={formik.values.extention}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      error={
                        formik.touched.extention &&
                        Boolean(formik.errors.extention)
                      }
                      helperText={
                        formik.touched.extention && formik.errors.extention
                      }
                    />
                  </Grid>
                </Grid>

                {/* Email */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  Business Email <Asterisk />
                </Typography>
                <TextField
                  name="email"
                  placeholder="Your Email"
                  fullWidth
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                  sx={{ marginTop: "5px" }}
                />

                {/* Company Name */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  Company Name
                </Typography>
                <TextField
                  name="companyName"
                  placeholder="Company Name"
                  fullWidth
                  value={formik.values.companyName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  sx={{ marginTop: "5px" }}
                />

                {/* Company Size */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  Company Size
                </Typography>
                <TextField
                  name="companySize"
                  select
                  fullWidth
                  value={formik.values.companySize}
                  onChange={formik.handleChange}
                  sx={{ marginTop: "5px" }}
                >
                  {companySizes.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Industry */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  Industry
                </Typography>
                <TextField
                  name="industry"
                  select
                  fullWidth
                  value={formik.values.industry}
                  onChange={formik.handleChange}
                  sx={{ marginTop: "5px" }}
                >
                  {industries.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                {/* Message */}
                <Typography color="inherit" textAlign="left" marginTop="20px">
                  Message <Asterisk />
                </Typography>
                <TextField
                  name="message"
                  placeholder="What do you want to discuss?"
                  multiline
                  rows={3}
                  fullWidth
                  value={formik.values.message}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.message && Boolean(formik.errors.message)
                  }
                  helperText={formik.touched.message && formik.errors.message}
                  sx={{ marginTop: "5px" }}
                />

                {/* Submit Button */}
                <div
                  style={{
                    marginTop: "15px",
                    textAlign: "left",
                    marginBottom: "40px",
                  }}
                >
                  <Button
                    data-testid="btn-sendmessage"
                    className="customSubmitBtn"
                    variant="contained"
                    sx={{
                      fontSize: "17px",
                      borderRadius: "8px",
                      backgroundColor: "#4385FF",
                    }}
                    disabled={isSubmitting}
                    endIcon={
                      isSubmitting && (
                        <CircularProgress size="16px" color="inherit" />
                      )
                    }
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </Grid>
          </Grid>
        </Container>
      </Container>
    </ThemeProvider>
  );
};

export default ContactPage;
